import React, { useEffect, useState } from "react";

import {
  AlertCircle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Clock,
  Eye,
  FileText,
  FileX,
  Link,
  MoreHorizontal,
  RefreshCw,
  Search,
} from "lucide-react";

import BSaleDocumentButton from "~/components/BSaleDocumentButton";
import { CreditNoteType } from "~/components/credit-notes/CreditNoteTypeSelector";
import { EnhancedCreditNoteDialog } from "~/components/credit-notes/EnhancedCreditNoteDialog";
import { NullifyCreditNoteDialog } from "~/components/credit-notes/NullifyCreditNoteDialog";
import { PartialCreditItem } from "~/components/credit-notes/PartialCreditNoteSelector";
import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { useToast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { bsaleFetch } from "~/services/bsaleProxy";
import { AssociateFacturaDialog } from "~/pages/facturas/components/AssociateFacturaDialog";
import OrderDetailDialog from "~/pages/purchase-orders/OrderDetailDialog";
import { SalesRequest, SalesRequestItem } from "~/types/client";

interface BSaleDocument {
  id: number;
  emissionDate: number;
  expirationDate: number;
  generationDate: number;
  number: number;
  totalAmount: number;
  netAmount: number;
  taxAmount: number;
  exemptAmount: number;
  state: number;
  commercialState: number;
  informedSii: number;
  responseMsgSii: string;
  address: string;
  municipality: string;
  city: string;
  urlPdf: string;
  urlPublicView: string;
  token: string;
  cancelled?: boolean;
  document_type: {
    href: string;
    id: string;
  };
  client: {
    href: string;
    id: string;
  };
}

interface BSaleResponse {
  href: string;
  count: number;
  limit: number;
  offset: number;
  items: BSaleDocument[];
  next?: string;
}

interface DocumentDetail {
  id: number;
  lineNumber: number;
  quantity: number;
  netUnitValue: number;
  netAmount: number;
  variant: {
    description: string;
    code: string;
  };
}

const BSaleFacturasPage: React.FC = () => {
  const [documents, setDocuments] = useState<BSaleDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [siiFilter, setSiiFilter] = useState<string>("all");
  const [documentTypeFilter, setDocumentTypeFilter] = useState<string>("all");

  // Initialize currentPage from URL parameter or default to 1
  const getInitialPage = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get("page");
    const page = pageParam ? parseInt(pageParam, 10) : 1;
    return page > 0 ? page : 1;
  };

  const [currentPage, setCurrentPage] = useState(getInitialPage());
  const [totalCount, setTotalCount] = useState(0);
  const [limit] = useState(50);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const { toast } = useToast();

  // Sales request drawer state
  const [selectedSalesRequest, setSelectedSalesRequest] =
    useState<SalesRequest | null>(null);
  const [salesRequestItems, setSalesRequestItems] = useState<
    SalesRequestItem[]
  >([]);
  const [clientNames, setClientNames] = useState<Record<string, string>>({});
  const [loadingSalesRequest, setLoadingSalesRequest] = useState(false);

  // Credit note nullification state
  const [showNullifyDialog, setShowNullifyDialog] = useState(false);
  const [selectedCreditNote, setSelectedCreditNote] = useState<any>(null);
  const [associatedCreditNotes, setAssociatedCreditNotes] = useState<
    Set<number>
  >(new Set());
  const [associatedFacturas, setAssociatedFacturas] = useState<Set<number>>(
    new Set()
  );

  // Credit note creation state for BSale facturas
  const [creditNoteDialogOpen, setCreditNoteDialogOpen] = useState(false);
  const [selectedFacturaForCreditNote, setSelectedFacturaForCreditNote] =
    useState<BSaleDocument | null>(null);

  // Manual factura ↔ sales-note association state (orphaned facturas)
  const [associateFacturaOpen, setAssociateFacturaOpen] = useState(false);
  const [facturaToAssociate, setFacturaToAssociate] =
    useState<BSaleDocument | null>(null);

  // Test client tracking state
  const [testClientDocuments, setTestClientDocuments] = useState<Set<number>>(
    new Set()
  );
  const [documentDetails, setDocumentDetails] = useState<DocumentDetail[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [processingCreditNote, setProcessingCreditNote] = useState(false);

  const fetchDocuments = async (page = 1) => {
    setLoading(true);
    try {
      // Build query parameters for document type filtering
      const getQueryParams = () => {
        const params = new URLSearchParams();
        if (documentTypeFilter === "factura") {
          params.append("documenttypeid", "5");
        } else if (documentTypeFilter === "nota_credito") {
          params.append("documenttypeid", "2");
        }
        return params.toString();
      };

      const queryParams = getQueryParams();
      const queryString = queryParams ? `&${queryParams}` : "";

      // First, get total count by fetching with limit=1
      const countResponse = await bsaleFetch(
        `/documents.json?limit=1&offset=0${queryString}`
      );

      if (!countResponse.ok) {
        throw new Error("Failed to fetch document count");
      }

      const countData: BSaleResponse = await countResponse.json();
      const totalCount = countData.count;
      setTotalCount(totalCount);

      // Calculate offset from the back
      // Page 1 should show the newest documents (highest offset)
      const calculatedOffset = Math.max(0, totalCount - page * limit);

      // Now fetch the actual documents with the calculated offset
      const response = await bsaleFetch(
        `/documents.json?limit=${limit}&offset=${calculatedOffset}${queryString}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch documents");
      }

      const data: BSaleResponse = await response.json();
      setDocuments(data.items);

      // Mark initial load as complete after first successful fetch
      if (isInitialLoad) {
        setIsInitialLoad(false);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los documentos de BSALE",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Effect to sync currentPage with URL parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get("page");
    const urlPage = pageParam ? parseInt(pageParam, 10) : 1;
    const validUrlPage = urlPage > 0 ? urlPage : 1;

    if (validUrlPage !== currentPage) {
      setCurrentPage(validUrlPage);
    }
  }, []);

  // Effect to fetch documents when currentPage changes
  useEffect(() => {
    if (currentPage > 0) {
      fetchDocuments(currentPage);
    }
  }, [currentPage]);

  // Effect to handle document type filter changes
  useEffect(() => {
    // Don't reset page during initial load - let URL parameter take precedence
    if (isInitialLoad) {
      return;
    }

    // Reset to page 1 and refetch when document type filter changes (after initial load)
    if (currentPage !== 1) {
      setCurrentPage(1);
      // Also update URL to remove page parameter
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete("page");
      window.history.replaceState({}, "", newUrl.toString());
      // fetchDocuments will be called by the currentPage useEffect
    } else {
      // If already on page 1, currentPage won't change so we need to fetch manually
      fetchDocuments(1);
    }
  }, [documentTypeFilter]);

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString("es-CL");
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
    }).format(amount);
  };

  const getSiiBadge = (
    informedSii: number,
    responseMsgSii: string,
    cancelled?: boolean
  ) => {
    // If document is cancelled, show red "Anulado" badge
    if (cancelled) {
      return (
        <div className="flex items-center gap-1">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <Badge variant="outline" className="border-red-600 text-red-600">
            Anulado
          </Badge>
        </div>
      );
    }

    switch (informedSii) {
      case 0:
        return (
          <div className="flex items-center gap-1">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <Badge
              variant="outline"
              className="border-green-600 text-green-600"
            >
              Exitoso
            </Badge>
          </div>
        );
      case 2:
        return (
          <div className="flex items-center gap-1">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <Badge variant="outline" className="border-red-600 text-red-600">
              Error
            </Badge>
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4 text-yellow-600" />
            <Badge
              variant="outline"
              className="border-yellow-600 text-yellow-600"
            >
              Pendiente
            </Badge>
          </div>
        );
    }
  };

  const getDocumentTypeBadge = (documentTypeId: string) => {
    switch (documentTypeId) {
      case "5":
        return (
          <Badge variant="outline" className="border-blue-600 text-blue-600">
            Factura
          </Badge>
        );
      case "2":
        return (
          <Badge
            variant="outline"
            className="border-purple-600 text-purple-600"
          >
            Nota de Crédito
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="border-gray-600 text-gray-600">
            Otro
          </Badge>
        );
    }
  };

  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch =
      doc.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.municipality.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && doc.state === 1) ||
      (statusFilter === "inactive" && doc.state === 0);

    const matchesSii =
      siiFilter === "all" ||
      (siiFilter === "success" && doc.informedSii === 0) ||
      (siiFilter === "error" && doc.informedSii === 2) ||
      (siiFilter === "pending" && doc.informedSii === 1);

    return matchesSearch && matchesStatus && matchesSii;
  });

  const totalPages = Math.ceil(totalCount / limit);

  // Validate currentPage when totalPages becomes available
  React.useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      // Directly set the page to avoid circular dependency
      setCurrentPage(totalPages);
      // Update URL
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set("page", totalPages.toString());
      window.history.replaceState({}, "", newUrl.toString());
    }
  }, [totalPages, currentPage]);

  const handlePageChange = (page: number) => {
    // Validate page bounds - but only if we have totalPages data
    const maxPage = totalPages > 0 ? totalPages : page; // Don't limit if data hasn't loaded yet
    const validPage = Math.max(1, Math.min(page, maxPage));

    setCurrentPage(validPage);

    // Update URL parameter
    const newUrl = new URL(window.location.href);
    if (validPage === 1) {
      // Remove page parameter for page 1 to keep URLs clean
      newUrl.searchParams.delete("page");
    } else {
      newUrl.searchParams.set("page", validPage.toString());
    }
    window.history.replaceState({}, "", newUrl.toString());

    // fetchDocuments will be called by the useEffect hook when currentPage changes
    // Removing direct call to avoid double-fetching
  };

  const handleRefresh = () => {
    fetchDocuments(currentPage);
  };

  const openPdf = (url: string) => {
    window.open(url, "_blank");
  };

  const handleOpenSalesRequest = (salesRequestId: string) => {
    fetchAndOpenSalesRequest(salesRequestId);
  };

  // Function to fetch sales request details and open drawer
  const fetchAndOpenSalesRequest = async (salesRequestId: string) => {
    setLoadingSalesRequest(true);
    try {
      // Fetch the sales request details
      const { data: salesRequestData, error: salesRequestError } =
        await supabase
          .from("sales_request")
          .select(
            `
          *,
          clients:client_id(id, name, risk_checked),
          profiles:created_by(id, name, lastname, email, role)
        `
          )
          .eq("id", salesRequestId)
          .single();

      if (salesRequestError) {
        throw salesRequestError;
      }

      if (!salesRequestData) {
        throw new Error("Sales request not found");
      }

      // Transform to frontend format
      const formattedSalesRequest: SalesRequest = {
        id: salesRequestData.id,
        clientId: salesRequestData.client_id,
        createdBy: salesRequestData.created_by,
        approvedRejectedBy: salesRequestData.approved_rejected_by || undefined,
        status: salesRequestData.status as SalesRequest["status"],
        paid: salesRequestData.paid,
        paymentTerms: salesRequestData.payment_terms,
        paymentType: salesRequestData.payment_type as "future" | "prepaid",
        paymentDueDate: salesRequestData.payment_due_date,
        totalAmount: salesRequestData.total_amount,
        comments: salesRequestData.comments || undefined,
        internal_notes: salesRequestData.internal_notes || undefined,
        factura_observations:
          salesRequestData.factura_observations || undefined,
        deliveryDate: salesRequestData.delivery_date || undefined,
        deliveryAddressId: salesRequestData.delivery_address_id || undefined,
        deliveryStreet: salesRequestData.delivery_street || undefined,
        deliveryNumber: salesRequestData.delivery_number || undefined,
        deliveryComplement: salesRequestData.delivery_complement || undefined,
        deliveryCity: salesRequestData.delivery_city || undefined,
        deliveryState: salesRequestData.delivery_state || undefined,
        deliveryComuna: salesRequestData.delivery_comuna || undefined,
        rejectedReason: salesRequestData.rejected_reason || undefined,
        createdAt: salesRequestData.created_at,
        updatedAt: salesRequestData.updated_at,
        salesConditionId: salesRequestData.sales_condition_id || undefined,
      };

      // Fetch sales request items
      const { data: itemsData, error: itemsError } = await supabase
        .from("sales_request_items")
        .select("*")
        .eq("sales_request_id", salesRequestId);

      if (itemsError) {
        throw itemsError;
      }

      // Transform items to frontend format
      const formattedItems: SalesRequestItem[] = (itemsData || []).map(
        (item) => ({
          id: item.id,
          salesRequestId: item.sales_request_id,
          productId: item.product_id,
          quantity: item.quantity,
          unitPrice: item.unit_price,
          discountPercent: item.discount_percent ?? undefined,
          createdAt: item.created_at,
        })
      );

      // Set client name
      const clientName =
        salesRequestData.clients?.name || "Cliente desconocido";
      setClientNames((prev) => ({
        ...prev,
        [formattedSalesRequest.clientId]: clientName,
      }));

      // Set the selected sales request and items
      setSelectedSalesRequest(formattedSalesRequest);
      setSalesRequestItems(formattedItems);
    } catch (error) {
      console.error("Error fetching sales request:", error);
      toast({
        title: "Error",
        description: "No se pudo cargar la información de la nota de venta",
        variant: "destructive",
      });
    } finally {
      setLoadingSalesRequest(false);
    }
  };

  // Helper functions for OrderDetailDialog
  const getSellerInfo = (sellerId: string) => {
    // For now, return default values - this could be enhanced to fetch actual seller info
    return {
      name: "Vendedor",
      lastname: "",
      email: "",
    };
  };

  const getApproverRejecterInfo = (approverId?: string) => {
    // For now, return default values - this could be enhanced to fetch actual approver info
    return {
      name: "Sistema",
      lastname: "",
      email: "",
      role: "system",
    };
  };

  // Function to handle credit note nullification
  const handleNullifyCreditNote = async (doc: BSaleDocument) => {
    try {
      // First, we need to find the corresponding credit note in our database
      const { data: creditNotes, error } = await supabase
        .from("sales_request_documents")
        .select("*")
        .eq("bsale_number", doc.number)
        .eq("document_type", "nota_de_credito")
        .single();

      if (error || !creditNotes) {
        toast({
          title: "Error",
          description:
            "No se pudo encontrar la nota de crédito en la base de datos",
          variant: "destructive",
        });
        return;
      }

      setSelectedCreditNote(creditNotes);
      setShowNullifyDialog(true);
    } catch (error) {
      console.error("Error finding credit note:", error);
      toast({
        title: "Error",
        description: "Error al buscar la nota de crédito",
        variant: "destructive",
      });
    }
  };

  // Handle successful nullification
  const handleNullificationSuccess = (result: any) => {
    console.log("Credit note nullified successfully:", result);
    toast({
      title: "Éxito",
      description: "La nota de crédito ha sido anulada correctamente",
    });

    // Refresh the documents to show updated status
    handleRefresh();

    // Close dialog
    setShowNullifyDialog(false);
    setSelectedCreditNote(null);
  };

  // Check if a document can be nullified
  const canNullifyDocument = (doc: BSaleDocument): boolean => {
    // Only credit notes can be nullified
    if (doc.document_type.id !== "2") {
      return false;
    }

    // Can't nullify if already cancelled
    if (doc.cancelled) {
      return false;
    }

    // Can't nullify if state is 0 (already cancelled)
    if (doc.state === 0) {
      return false;
    }

    return true;
  };

  // Check which credit notes are already associated
  const checkAssociatedCreditNotes = async () => {
    if (documents.length === 0) return;

    const creditNotes = documents.filter((doc) => doc.document_type.id === "2");
    const creditNoteNumbers = creditNotes.map((doc) => doc.number.toString());

    if (creditNoteNumbers.length === 0) return;

    const { data: existingDocs } = await supabase
      .from("sales_request_documents")
      .select("bsale_number")
      .eq("document_type", "nota_de_credito")
      .in("bsale_number", creditNoteNumbers);

    const associatedNumbers = new Set(
      existingDocs?.map((doc) => parseInt(doc.bsale_number)) || []
    );

    setAssociatedCreditNotes(associatedNumbers);
  };

  // Check which facturas are already associated with sales requests
  const checkAssociatedFacturas = async () => {
    if (documents.length === 0) return;

    const facturas = documents.filter((doc) => doc.document_type.id === "5");
    const facturaNumbers = facturas.map((doc) => doc.number.toString());

    if (facturaNumbers.length === 0) return;

    const { data: existingDocs } = await supabase
      .from("sales_request_documents")
      .select("bsale_number")
      .eq("document_type", "factura")
      .in("bsale_number", facturaNumbers);

    const associatedNumbers = new Set(
      existingDocs?.map((doc) => parseInt(doc.bsale_number)) || []
    );

    setAssociatedFacturas(associatedNumbers);
  };

  useEffect(() => {
    checkAssociatedCreditNotes();
    checkAssociatedFacturas();
    checkTestClientDocuments();
  }, [documents]);

  // Function to check which documents are from test clients
  const checkTestClientDocuments = async () => {
    const testClientDocs = new Set<number>();

    // Check each document in batches to avoid overwhelming the system
    const batchSize = 10;
    for (let i = 0; i < documents.length; i += batchSize) {
      const batch = documents.slice(i, i + batchSize);

      const batchPromises = batch.map(async (doc) => {
        try {
          const isTest = await isBSaleDocumentFromTestClient(doc);
          if (isTest) {
            testClientDocs.add(doc.number);
          }
        } catch (error) {
          console.error(
            `Error checking test client status for document ${doc.number}:`,
            error
          );
        }
      });

      await Promise.all(batchPromises);
    }

    setTestClientDocuments(testClientDocs);
  };

  // Function to associate a credit note with a sales request
  const handleAssociateCreditNote = async (doc: BSaleDocument) => {
    try {
      // First, fetch the credit note details from BSale to get the reference document
      const creditNoteResponse = await bsaleFetch(
        `/documents/${doc.id}.json`
      );

      if (!creditNoteResponse.ok) {
        throw new Error("Failed to fetch credit note details from BSale");
      }

      const creditNoteData = await creditNoteResponse.json();

      // Look for reference document in the credit note
      let originalFacturaNumber = null;

      // Method 1: Try to get from references API
      if (creditNoteData.references && creditNoteData.references.href) {
        try {
          const referencesResponse = await bsaleFetch(
            creditNoteData.references.href
          );

          if (referencesResponse.ok) {
            const referencesData = await referencesResponse.json();
            // The references API returns an object with items array
            const references = referencesData.items || [];

            // Try to find the original factura from references
            for (const ref of references) {
              if (ref.number) {
                originalFacturaNumber = ref.number;
                break;
              }
            }
          }
        } catch (referencesError) {
          console.error("Error fetching references:", referencesError);
        }
      }

      // Method 2: Look for other fields that might contain the factura reference
      if (!originalFacturaNumber) {
        // Check if there are any reference-related fields in the main document
        if (creditNoteData.reference) {
          originalFacturaNumber =
            creditNoteData.reference.number || creditNoteData.reference;
        }

        // Check for return-related fields (credit notes are often created as returns)
        if (!originalFacturaNumber && creditNoteData.return) {
          // If it's a return document, try to get the reference from there
        }

        // Check document details for any reference information
        if (
          !originalFacturaNumber &&
          creditNoteData.details &&
          creditNoteData.details.href
        ) {
          try {
            const detailsResponse = await bsaleFetch(
              creditNoteData.details.href
            );

            if (detailsResponse.ok) {
              const detailsData = await detailsResponse.json();
              // Sometimes the original document reference is in the details
            }
          } catch (detailsError) {
            console.error("Error fetching details:", detailsError);
          }
        }
      }

      if (!originalFacturaNumber) {
        // Ask user to manually input the factura number
        const manualFacturaNumber = prompt(
          `La nota de crédito #${doc.number} no tiene una referencia automática a una factura en BSale.\n\n` +
            `Si conoces el número de la factura original, ingrésalo aquí para crear la asociación manualmente:\n\n` +
            `Número de factura:`
        );

        if (manualFacturaNumber && manualFacturaNumber.trim()) {
          originalFacturaNumber = manualFacturaNumber.trim();
        } else {
          toast({
            title: "Asociación Cancelada",
            description: `No se ingresó un número de factura válido. La asociación de la nota de crédito #${doc.number} ha sido cancelada.`,
            variant: "destructive",
          });
          return;
        }
      }

      // Find the sales request associated with this factura
      const { data: originalDocument, error: originalDocError } = await supabase
        .from("sales_request_documents")
        .select("sales_request_id, id")
        .eq("bsale_number", originalFacturaNumber)
        .eq("document_type", "factura")
        .single();

      if (originalDocError) {
        console.error(
          "Database error finding original factura:",
          originalDocError
        );
        toast({
          title: "Error de Base de Datos",
          description: `Error al buscar la factura #${originalFacturaNumber} en la base de datos. Contacta al administrador del sistema.`,
          variant: "destructive",
        });
        return;
      }

      if (!originalDocument) {
        toast({
          title: "Factura No Encontrada",
          description: `La factura #${originalFacturaNumber} referenciada por esta nota de crédito no se encuentra en nuestro sistema. Esto puede suceder si:
          
• La factura fue creada fuera del sistema de notas de venta
• La factura no ha sido sincronizada correctamente
• El número de factura es incorrecto

Por favor, verifica que la factura #${originalFacturaNumber} exista en el sistema antes de asociar esta nota de crédito.`,
          variant: "destructive",
        });
        return;
      }

      // Check if this credit note is already associated with a sales request
      const { data: existingCreditNote } = await supabase
        .from("sales_request_documents")
        .select("id")
        .eq("bsale_number", doc.number.toString())
        .eq("document_type", "nota_de_credito")
        .single();

      if (existingCreditNote) {
        toast({
          title: "Nota de Crédito Ya Asociada",
          description: `La nota de crédito #${doc.number} ya está asociada a una nota de venta. No es necesario asociarla nuevamente.`,
          variant: "destructive",
        });
        return;
      }

      // Create the sales_request_document entry for the credit note
      const { data: creditNoteDocument, error: insertError } = await supabase
        .from("sales_request_documents")
        .insert({
          sales_request_id: originalDocument.sales_request_id,
          document_type: "nota_de_credito",
          document_url: doc.urlPdf || doc.urlPublicView,
          filename: `nota-credito-${doc.number}.pdf`,
          content_type: "application/pdf",
          external_id: doc.id,
          bsale_number: doc.number.toString(),
          total_amount: doc.totalAmount,
          net_amount: doc.netAmount,
          tax_amount: doc.taxAmount,
          url_public_view: doc.urlPublicView,
          url_pdf: doc.urlPdf,
          url_xml: creditNoteData.urlXml || null,
          token: doc.token,
          generation_date: doc.generationDate,
          state: doc.state,
          emission_date: doc.emissionDate,
          response_data: creditNoteData,
          original_document_id: originalDocument.id,
        })
        .select()
        .single();

      if (insertError) {
        console.error("Database error inserting credit note:", insertError);
        toast({
          title: "Error al Guardar",
          description: `Error al guardar la asociación de la nota de crédito #${doc.number}. Contacta al administrador del sistema.`,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Éxito",
        description: `La nota de crédito #${doc.number} ha sido asociada correctamente a la factura #${originalFacturaNumber}`,
      });

      // Update the associated credit notes state
      setAssociatedCreditNotes((prev) => new Set(prev).add(doc.number));

      // Refresh the page to show the updated association
      handleRefresh();
    } catch (error) {
      console.error("Error associating credit note:", error);

      // Provide more specific error messages based on the error type
      let errorMessage = "Error inesperado al asociar la nota de crédito";

      if (error instanceof Error) {
        if (error.message.includes("Failed to fetch")) {
          errorMessage =
            "Error de conexión con BSale. Verifica tu conexión a internet e intenta nuevamente.";
        } else if (error.message.includes("credit note details")) {
          errorMessage =
            "No se pudo obtener los detalles de la nota de crédito desde BSale. Verifica que el documento existe.";
        }
      }

      toast({
        title: "Error de Asociación",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  // Check if a credit note can be associated
  const canAssociateCreditNote = (doc: BSaleDocument): boolean => {
    // Only credit notes that are not already associated
    return (
      doc.document_type.id === "2" && !associatedCreditNotes.has(doc.number)
    );
  };

  // Check if an orphaned factura can be linked to a sales note ("No asociado a NV").
  const canAssociateFacturaToNV = (doc: BSaleDocument): boolean => {
    return (
      doc.document_type.id === "5" &&
      !doc.cancelled &&
      !associatedFacturas.has(doc.number)
    );
  };

  // Function to fetch document details from BSale
  const fetchDocumentDetails = async (
    doc: BSaleDocument
  ): Promise<DocumentDetail[]> => {
    setLoadingDetails(true);
    try {
      const response = await bsaleFetch(
        `/documents/${doc.id}/details.json`
      );

      if (!response.ok) {
        throw new Error(
          `Error al obtener detalles del documento: ${response.status}`
        );
      }

      const detailsData = await response.json();
      return detailsData.items || [];
    } catch (error) {
      console.error("Error fetching document details:", error);
      throw error;
    } finally {
      setLoadingDetails(false);
    }
  };

  // A Chilean rut is only complete/valid when it carries a check digit (0-9 or
  // K) after the hyphen. Test clients use an INCOMPLETE rut ("11111111-") so the
  // SII never accepts their documents; credit notes must inherit that.
  const isCompleteRut = (rut?: string | null): boolean => {
    if (!rut) return false;
    const dv = rut.trim().split("-").pop()?.trim() ?? "";
    return /^[0-9kK]$/.test(dv);
  };

  // Fetch the original factura's BSale client, plus whether our DB flags it as a
  // test client. Returns a null client if BSale can't be reached.
  const fetchBSaleClientInfo = async (
    doc: BSaleDocument
  ): Promise<{ bsaleClient: any | null; isTest: boolean }> => {
    try {
      const clientResponse = await bsaleFetch(doc.client.href);

      if (!clientResponse.ok) {
        console.warn(
          `Could not fetch BSale client info for document ${doc.number}`
        );
        return { bsaleClient: null, isTest: false };
      }

      const bsaleClient = await clientResponse.json();
      const clientCode = bsaleClient?.code;

      if (!clientCode) {
        return { bsaleClient, isTest: false };
      }

      // A rut can map to more than one row in our DB; treat it as a test client
      // if ANY matching row is flagged is_test.
      const { data: matches } = await supabase
        .from("clients")
        .select("is_test")
        .eq("tax_id", clientCode);

      const isTest =
        Array.isArray(matches) && matches.some((c) => c.is_test === true);

      return { bsaleClient, isTest };
    } catch (error) {
      console.error(
        "Error checking if BSale document is from test client:",
        error
      );
      return { bsaleClient: null, isTest: false };
    }
  };

  // Thin wrapper kept for the batch test-client check.
  const isBSaleDocumentFromTestClient = async (
    doc: BSaleDocument
  ): Promise<boolean> => {
    const { isTest } = await fetchBSaleClientInfo(doc);
    return isTest;
  };

  // Function to create standalone credit note for BSale factura (no temporary factura needed)
  const createStandaloneCreditNote = async (
    doc: BSaleDocument,
    params: {
      motive: string;
      credit_note_type: CreditNoteType;
      partial_items?: PartialCreditItem[];
      administrative_amount?: number;
      administrative_reason?: string;
      target_detail_index?: number;
    }
  ) => {
    // First, fetch the document details from BSale
    const detailsResponse = await bsaleFetch(
      `/documents/${doc.id}/details.json`
    );

    if (!detailsResponse.ok) {
      throw new Error(
        `Failed to fetch document details: ${detailsResponse.status}`
      );
    }

    const detailsData = await detailsResponse.json();
    const documentDetails = detailsData.items || [];

    // Prepare details array based on credit note type
    let details: Array<{
      documentDetailId: number;
      quantity: number;
      unitValue: number;
    }> = [];

    switch (params.credit_note_type) {
      case "full":
        // Include all document details for full credit note
        details = documentDetails.map((detail: any) => ({
          documentDetailId: detail.id,
          quantity: detail.quantity,
          unitValue: detail.netUnitValue,
        }));
        break;

      case "partial":
        // Include only selected items for partial credit note
        if (params.partial_items && params.partial_items.length > 0) {
          details = params.partial_items.map((item) => ({
            documentDetailId: item.document_detail_id,
            quantity: item.quantity,
            unitValue: item.unit_value || 0,
          }));
        }
        break;

      case "administrative": {
        // Attach the flat credit to the chosen line (defaults to the first).
        const targetDetail = documentDetails[params.target_detail_index ?? 0];
        if (targetDetail && params.administrative_amount) {
          // Gross up so the referenced line's discount doesn't shrink the net:
          // BSale inherits the line discount and applies it to unitValue.
          const lineDiscountPct = Number(targetDetail.discountPercentage) || 0;
          const unitValue =
            lineDiscountPct > 0 && lineDiscountPct < 100
              ? Math.round(
                  params.administrative_amount / (1 - lineDiscountPct / 100)
                )
              : params.administrative_amount;
          details = [
            {
              documentDetailId: targetDetail.id,
              quantity: 1,
              unitValue,
            },
          ];
        }
        break;
      }
    }

    // Fetch the ORIGINAL factura's BSale client so the credit note is emitted
    // under the exact same rut as the factura. For test facturas that rut is
    // incomplete ("11111111-"), which is what keeps them off the SII — we must
    // never substitute a valid generic rut ("1-7") here.
    const { bsaleClient: facturaClient, isTest: isTestClient } =
      await fetchBSaleClientInfo(doc);

    if (!facturaClient?.code) {
      throw new Error(
        "No se pudo obtener el cliente de la factura original en BSale para emitir la nota de crédito"
      );
    }

    // Never declare to the SII for test clients or incomplete/invalid ruts.
    const declareSii =
      isTestClient || !isCompleteRut(facturaClient.code) ? 0 : 1;

    // Call BSale API to create the credit note using the correct endpoint
    const bsaleResponse = await bsaleFetch("/returns.json", {
      method: "POST",
      body: {
        documentTypeId: 2, // Credit note type
        officeId: 1,
        referenceDocumentId: doc.id,
        expirationDate: Math.floor(Date.now() / 1000),
        emissionDate: Math.floor(Date.now() / 1000),
        motive: params.motive,
        declareSii, // 0 for test / incomplete-rut clients
        priceAdjustment: params.credit_note_type === "administrative" ? 1 : 0,
        editTexts: 0,
        // full/administrative = 0 ("devolución dinero"), partial = 1. Admin
        // uses 0 (not 2 "abono línea de crédito"): BSale only allows type 2 for
        // facturas that have a credit payment method. priceAdjustment (above)
        // is what marks the administrative NC as a price adjustment.
        type: params.credit_note_type === "partial" ? 1 : 0,
        client: {
          code: facturaClient.code,
          municipality: facturaClient.municipality || "Santiago",
          activity: facturaClient.activity || "Actividad comercial",
          company: facturaClient.company || "Cliente",
          city: facturaClient.city || "SANTIAGO",
          email: facturaClient.email || "cliente@example.com",
          address: facturaClient.address || "Dirección no especificada",
        },
        details: details, // Now properly populated with actual details
        dynamicAttributes: [
          {
            description: `Nota de crédito BSale${isTestClient ? " (Cliente de prueba)" : ""}`,
            dynamicAttributeId: 6,
          },
        ],
      },
    });

    if (!bsaleResponse.ok) {
      const errorData = await bsaleResponse.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Error creating credit note in BSale"
      );
    }

    const bsaleData = await bsaleResponse.json();

    // Calculate proper net_amount and tax_amount from BSale response
    let creditNetAmount = 0;
    let creditTaxAmount = 0;
    const creditTotalAmount = bsaleData.amount || 0;

    // Try to extract from BSale credit note response first
    if (bsaleData.credit_note) {
      creditNetAmount =
        bsaleData.credit_note.netAmount ||
        bsaleData.credit_note.net_amount ||
        0;
      creditTaxAmount =
        bsaleData.credit_note.taxAmount ||
        bsaleData.credit_note.tax_amount ||
        0;
    }

    // If not available from BSale response, calculate from total amount
    if (!creditNetAmount && creditTotalAmount > 0) {
      // Calculate net amount by removing Chilean IVA (19%)
      creditNetAmount = Math.round(creditTotalAmount / 1.19);
      creditTaxAmount = creditTotalAmount - creditNetAmount;
    }

    // Store only the credit note document in our database
    const { data: creditNoteDoc, error: insertError } = await supabase
      .from("sales_request_documents")
      .insert({
        sales_request_id: null, // No sales request associated
        document_type: "nota_de_credito",
        document_url: bsaleData.credit_note?.urlPdf || null,
        filename: `credit-note-${bsaleData.credit_note?.number}.pdf`,
        content_type: "application/pdf",
        external_id: bsaleData.credit_note?.id,
        bsale_number: bsaleData.credit_note?.number?.toString(),
        total_amount: creditTotalAmount,
        net_amount: creditNetAmount,
        tax_amount: creditTaxAmount,
        response_data: bsaleData,
        is_temporary: true, // Mark as standalone BSale credit note
        credit_note_type: params.credit_note_type,
        motive: params.motive,
        // Note: No original_document_id since we're not creating the factura document
      })
      .select()
      .single();

    if (insertError) {
      throw insertError;
    }

    return creditNoteDoc;
  };

  // Function to open credit note dialog for BSale factura
  const openCreditNoteDialogForBSaleFactura = async (doc: BSaleDocument) => {
    setSelectedFacturaForCreditNote(doc);

    try {
      const details = await fetchDocumentDetails(doc);
      setDocumentDetails(details);
      setCreditNoteDialogOpen(true);
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron cargar los detalles del documento",
        variant: "destructive",
      });
    }
  };

  // Function to handle credit note creation for BSale factura
  const handleCreditNoteSubmitForBSaleFactura = async (params: {
    motive: string;
    credit_note_type: CreditNoteType;
    partial_items?: PartialCreditItem[];
    administrative_amount?: number;
    administrative_reason?: string;
    target_detail_index?: number;
  }) => {
    if (!selectedFacturaForCreditNote) return;

    setProcessingCreditNote(true);
    try {
      // Create credit note directly via BSale API and store only the credit note document
      await createStandaloneCreditNote(selectedFacturaForCreditNote, params);

      toast({
        title: "Éxito",
        description: "Nota de crédito generada exitosamente",
      });

      // Update the associated facturas state since we created a credit note
      setAssociatedFacturas((prev) =>
        new Set(prev).add(selectedFacturaForCreditNote.number)
      );

      // Close the dialog and refresh the documents list
      setCreditNoteDialogOpen(false);
      setSelectedFacturaForCreditNote(null);
      handleRefresh();
    } catch (error) {
      console.error("Error creating credit note:", error);
      toast({
        title: "Error",
        description: "No se pudo generar la nota de crédito",
        variant: "destructive",
      });
    } finally {
      setProcessingCreditNote(false);
    }
  };

  // Check if a BSale factura can have credit note created
  const canCreateCreditNoteForBSaleFactura = (doc: BSaleDocument): boolean => {
    // Only show for facturas that are:
    // 1. Type "5" (factura)
    // 2. Not cancelled
    // 3. NOT associated with any sales request (will show "No asociado a NV")
    // Note: Removed state check since recreated facturas might have state 0 but are still valid
    if (doc.document_type.id !== "5") {
      return false;
    }

    if (doc.cancelled) {
      return false;
    }

    // Removed state === 0 check since recreated facturas might have state 0
    // but are still valid for credit note creation

    if (associatedFacturas.has(doc.number)) {
      return false;
    }

    return true;
  };

  return (
    <PageContainer>
      <PageHeader
        title="Documentos BSALE"
        description="Documentos sincronizados desde BSALE"
      />

      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Buscar</label>
              <div className="relative">
                <Search className="absolute left-2 top-2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Dirección, comuna..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-9 pl-8"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Estado</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="active">Activos</SelectItem>
                  <SelectItem value="inactive">Inactivos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Estado SII</label>
              <Select value={siiFilter} onValueChange={setSiiFilter}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="success">Exitosos</SelectItem>
                  <SelectItem value="error">Con Errores</SelectItem>
                  <SelectItem value="pending">Pendientes</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Tipo de Documento</label>
              <Select
                value={documentTypeFilter}
                onValueChange={setDocumentTypeFilter}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="factura">Factura</SelectItem>
                  <SelectItem value="nota_credito">Nota de Crédito</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end sm:col-span-2 lg:col-span-4">
              <Button
                onClick={handleRefresh}
                disabled={loading}
                className="h-9 w-full sm:w-auto"
              >
                <RefreshCw
                  className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`}
                />
                Actualizar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Documents Table */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Documentos</CardTitle>
          <CardDescription>
            Documentos sincronizados desde BSALE ({filteredDocuments.length} de{" "}
            {totalCount})
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6">
              <TableSkeleton rows={8} columns={8} />
            </div>
          ) : (
            <div className="space-y-4 p-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número</TableHead>
                    <TableHead>Nota de Venta</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Fecha Emisión</TableHead>
                    <TableHead>Dirección</TableHead>
                    <TableHead>Monto Total</TableHead>
                    <TableHead>Estado SII</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDocuments.map((doc, index) => (
                    <TableRow
                      key={doc.id}
                      style={{
                        animationDelay: `${Math.min(index * 40, 400)}ms`,
                      }}
                      className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          #{doc.number}
                          {testClientDocuments.has(doc.number) && (
                            <Badge variant="secondary" className="text-xs">
                              Cliente de prueba
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <BSaleDocumentButton
                          bsaleNumber={doc.number}
                          onOpenSalesRequest={handleOpenSalesRequest}
                        />
                      </TableCell>
                      <TableCell>
                        {getDocumentTypeBadge(doc.document_type.id)}
                      </TableCell>
                      <TableCell>{formatDate(doc.emissionDate)}</TableCell>
                      <TableCell>
                        <div className="max-w-32 truncate">
                          {doc.address}, {doc.municipality}
                        </div>
                      </TableCell>
                      <TableCell>{formatCurrency(doc.totalAmount)}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {getSiiBadge(
                            doc.informedSii,
                            doc.responseMsgSii,
                            doc.cancelled
                          )}
                          {doc.informedSii === 2 && (
                            <div className="max-w-20 truncate text-xs text-red-600">
                              {doc.responseMsgSii}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <span className="sr-only">Abrir menú</span>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => openPdf(doc.urlPdf)}
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              Ver PDF
                            </DropdownMenuItem>
                            {canNullifyDocument(doc) && (
                              <DropdownMenuItem
                                onClick={() => handleNullifyCreditNote(doc)}
                                className="text-red-600 focus:text-red-600"
                              >
                                <FileX className="mr-2 h-4 w-4" />
                                Anular
                              </DropdownMenuItem>
                            )}
                            {canAssociateCreditNote(doc) && (
                              <DropdownMenuItem
                                onClick={() => handleAssociateCreditNote(doc)}
                                className="text-blue-600 focus:text-blue-600"
                              >
                                <Link className="mr-2 h-4 w-4" />
                                Asociar
                              </DropdownMenuItem>
                            )}
                            {canAssociateFacturaToNV(doc) && (
                              <DropdownMenuItem
                                onClick={() => {
                                  setFacturaToAssociate(doc);
                                  setAssociateFacturaOpen(true);
                                }}
                                className="text-blue-600 focus:text-blue-600"
                              >
                                <Link className="mr-2 h-4 w-4" />
                                Asociar a NV
                              </DropdownMenuItem>
                            )}
                            {canCreateCreditNoteForBSaleFactura(doc) && (
                              <DropdownMenuItem
                                onClick={() =>
                                  openCreditNoteDialogForBSaleFactura(doc)
                                }
                                className="text-green-600 focus:text-green-600"
                                disabled={loadingDetails}
                              >
                                <FileText className="mr-2 h-4 w-4" />
                                {loadingDetails
                                  ? "Cargando..."
                                  : "Crear Nota de Crédito"}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Página {currentPage} de {totalPages} ({totalCount} documentos
                  total)
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1 || loading}
                    title="Ir al inicio"
                  >
                    <ChevronsLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1 || loading}
                  >
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    Anterior
                  </Button>

                  {/* Page Numbers */}
                  <div className="flex items-center gap-1">
                    {(() => {
                      const pages = [];
                      const maxVisiblePages = 5;
                      let startPage = Math.max(
                        1,
                        currentPage - Math.floor(maxVisiblePages / 2)
                      );
                      const endPage = Math.min(
                        totalPages,
                        startPage + maxVisiblePages - 1
                      );

                      // Adjust start page if we're near the end
                      if (endPage - startPage + 1 < maxVisiblePages) {
                        startPage = Math.max(1, endPage - maxVisiblePages + 1);
                      }

                      // Add ellipsis at the beginning if needed
                      if (startPage > 1) {
                        pages.push(
                          <Button
                            key={1}
                            variant={currentPage === 1 ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(1)}
                            disabled={loading}
                            className="min-w-[2.5rem]"
                          >
                            1
                          </Button>
                        );
                        if (startPage > 2) {
                          pages.push(
                            <span
                              key="ellipsis-start"
                              className="px-2 text-sm text-muted-foreground"
                            >
                              ...
                            </span>
                          );
                        }
                      }

                      // Add visible page numbers
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <Button
                            key={i}
                            variant={currentPage === i ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(i)}
                            disabled={loading}
                            className="min-w-[2.5rem]"
                          >
                            {i}
                          </Button>
                        );
                      }

                      // Add ellipsis at the end if needed
                      if (endPage < totalPages) {
                        if (endPage < totalPages - 1) {
                          pages.push(
                            <span
                              key="ellipsis-end"
                              className="px-2 text-sm text-muted-foreground"
                            >
                              ...
                            </span>
                          );
                        }
                        pages.push(
                          <Button
                            key={totalPages}
                            variant={
                              currentPage === totalPages ? "default" : "outline"
                            }
                            size="sm"
                            onClick={() => handlePageChange(totalPages)}
                            disabled={loading}
                            className="min-w-[2.5rem]"
                          >
                            {totalPages}
                          </Button>
                        );
                      }

                      return pages;
                    })()}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || loading}
                  >
                    Siguiente
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages || loading}
                    title="Ir al final"
                  >
                    <ChevronsRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sales Request Detail Drawer */}
      {selectedSalesRequest && (
        <OrderDetailDialog
          selectedOrder={selectedSalesRequest}
          setSelectedOrder={setSelectedSalesRequest}
          getSellerInfo={getSellerInfo}
          getApproverRejecterInfo={getApproverRejecterInfo}
          clientNames={clientNames}
          items={salesRequestItems}
          onOrderStatusChange={() => {
            // Optionally refresh data after status change
          }}
        />
      )}

      {/* Credit Note Nullification Dialog */}
      {selectedCreditNote && (
        <NullifyCreditNoteDialog
          isOpen={showNullifyDialog}
          onClose={() => {
            setShowNullifyDialog(false);
            setSelectedCreditNote(null);
          }}
          creditNote={selectedCreditNote}
          onSuccess={handleNullificationSuccess}
        />
      )}

      {/* Credit Note Creation Dialog */}
      {creditNoteDialogOpen && (
        <EnhancedCreditNoteDialog
          isOpen={creditNoteDialogOpen}
          onClose={() => {
            setCreditNoteDialogOpen(false);
            setSelectedFacturaForCreditNote(null);
          }}
          onSubmit={handleCreditNoteSubmitForBSaleFactura}
          documentDetails={documentDetails}
          isSubmitting={processingCreditNote}
          documentNumber={selectedFacturaForCreditNote?.number}
          documentAmount={selectedFacturaForCreditNote?.totalAmount}
        />
      )}

      {/* Associate orphaned factura to a sales note */}
      <AssociateFacturaDialog
        open={associateFacturaOpen}
        onOpenChange={(open) => {
          setAssociateFacturaOpen(open);
          if (!open) setFacturaToAssociate(null);
        }}
        factura={facturaToAssociate}
        onAssociated={handleRefresh}
      />
    </PageContainer>
  );
};

export default BSaleFacturasPage;
